export const CONSENT_STORAGE_KEY = 'trimergy_cookie_consent';
export const CONSENT_VERSION = 1;
export const GA4_MEASUREMENT_ID = 'G-C0HXLRB7EG';

const DEFAULT_CONSENT = {
  essential: true,
  analytics: false,
  marketing: false,
};

export function getDefaultConsent() {
  return { ...DEFAULT_CONSENT };
}

export function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;

    return {
      essential: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      decidedAt: parsed.decidedAt || null,
    };
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent() {
  return Boolean(getStoredConsent()?.analytics);
}

export function hasMarketingConsent() {
  return Boolean(getStoredConsent()?.marketing);
}

export function saveConsent(consent) {
  const payload = {
    version: CONSENT_VERSION,
    essential: true,
    analytics: Boolean(consent.analytics),
    marketing: Boolean(consent.marketing),
    decidedAt: new Date().toISOString(),
  };

  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
  applyConsent(payload);
  return payload;
}

function pushConsentEvent(consent) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'cookie_consent_update',
    analytics_consent: consent.analytics ? 'granted' : 'denied',
    marketing_consent: consent.marketing ? 'granted' : 'denied',
  });
}

function updateGtagConsent(consent) {
  if (typeof window.gtag !== 'function') return;

  window.gtag('consent', 'update', {
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
    personalization_storage: consent.marketing ? 'granted' : 'denied',
  });
}

let ga4LoadScheduled = false;

export function loadGa4IfAllowed() {
  if (!hasAnalyticsConsent() || ga4LoadScheduled) return;
  ga4LoadScheduled = true;

  if (typeof window.gtag === 'function') {
    window.gtag('js', new Date());
    window.gtag('config', GA4_MEASUREMENT_ID);
  }

  const scheduleLoad = () => {
    setTimeout(() => {
      if (document.querySelector(`script[src*="gtag/js?id=${GA4_MEASUREMENT_ID}"]`)) return;

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
      document.head.appendChild(script);
    }, 3000);
  };

  if (document.readyState === 'complete') {
    scheduleLoad();
  } else {
    window.addEventListener('load', scheduleLoad, { once: true });
  }
}

export function applyConsent(consent) {
  updateGtagConsent(consent);
  pushConsentEvent(consent);

  if (consent.analytics) {
    loadGa4IfAllowed();
  }

  window.dispatchEvent(new CustomEvent('trimergy:cookie-consent', { detail: consent }));
}

export function acceptAllConsent() {
  return saveConsent({ essential: true, analytics: true, marketing: true });
}

export function rejectNonEssentialConsent() {
  return saveConsent({ essential: true, analytics: false, marketing: false });
}

export function restoreConsentFromStorage() {
  const stored = getStoredConsent();
  if (stored) {
    applyConsent(stored);
  }
  return stored;
}
